// SPV Pool v1.0 — TanStack Query hooks for SPV trusts (/api/spvs/*).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { spvTrustsApi } from "@/api/spvTrusts";
import type { SpvCreate, SpvListQuery, SpvUpdate } from "@/types/spvPool";

export const spvTrustKeys = {
  all: ["spv-trusts"] as const,
  lists: () => [...spvTrustKeys.all, "list"] as const,
  list: (query: SpvListQuery | undefined) =>
    [...spvTrustKeys.lists(), query ?? {}] as QueryKey,
  detail: (id: string) => [...spvTrustKeys.all, "detail", id] as const,
};

export function useSpvTrustList(query?: SpvListQuery, enabled = true) {
  return useQuery({
    queryKey: spvTrustKeys.list(query),
    queryFn: () => spvTrustsApi.list(query),
    enabled,
  });
}

export function useSpvTrust(id: string | undefined) {
  return useQuery({
    queryKey: spvTrustKeys.detail(id ?? ""),
    queryFn: () => spvTrustsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateSpvTrust() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpvCreate) => spvTrustsApi.create(body),
    onSuccess: (trust) => {
      qc.invalidateQueries({ queryKey: spvTrustKeys.lists() });
      qc.setQueryData(spvTrustKeys.detail(trust.id), trust);
    },
  });
}

export function useUpdateSpvTrust(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SpvUpdate) => spvTrustsApi.update(id, body),
    onSuccess: (trust) => {
      qc.setQueryData(spvTrustKeys.detail(id), trust);
      qc.invalidateQueries({ queryKey: spvTrustKeys.lists() });
    },
  });
}
